import { useEffect, useRef, useContext } from 'react';
import { SearchContext } from '../../search-filter/components/search-context';

/**
 * Custom hook to expose external API for AB testing team on SLP pages
 * Allows programmatic manipulation of SLP filters
 *
 * @param {string} searchKey - Unique identifier for this search instance
 */
const useSLPExternalAPI = searchKey => {
    const searchContext = useContext(SearchContext);
    const searchContextRef = useRef(searchContext);

    // Update ref when context changes
    useEffect(() => {
        searchContextRef.current = searchContext;
    });

    useEffect(() => {
        if (typeof window === 'undefined' || !searchKey) return;

        // Warn if window.hpSlpAPI already exists
        if (window.hpSlpAPI) {
            console.warn('SLP API: window.hpSlpAPI already exists and will be overwritten.');
        }

        /**
         * Transform VWA-style filter format to SLP format
         * VWA format: { mem: { '64-GB': '64-GB' }, scrs: { '14-': '14-' } }
         * SLP format: { 'facets.mem': ['64-GB'], 'facets.scrs': ['14-'] }
         */
        const transformVWAtoSLPFormat = vwaFilters => {
            const slpFilters = {};

            // Root filters that don't need 'facets.' prefix
            const rootFilters = ['price', 'cat', 'pg'];

            Object.keys(vwaFilters).forEach(key => {
                const value = vwaFilters[key];

                // Determine the SLP key (add 'facets.' prefix unless it's a root filter)
                const slpKey = rootFilters.includes(key) ? key : `facets.${key}`;

                // Convert value from VWA object format to SLP array format
                if (value && typeof value === 'object' && !Array.isArray(value)) {
                    // VWA format: { '64-GB': '64-GB' } -> SLP: ['64-GB']
                    slpFilters[slpKey] = Object.keys(value);
                } else if (Array.isArray(value)) {
                    // Already an array, use as-is
                    slpFilters[slpKey] = value;
                } else {
                    // Single value
                    slpFilters[slpKey] = [value];
                }
            });

            return slpFilters;
        };

        /**
         * External API for updating SLP filters programmatically
         * @param {Object} queryObject - Filter object in VWA format: { facetKey: { 'value': 'value' }, ... }
         *                               Example: { mem: { '64-GB': '64-GB' }, scrs: { '14-': '14-' } }
         * @param {boolean} replaceFilters - If true, replaces all current filters. If false, merges with existing filters. Default: true
         * @returns {Object} Updated filter state
         */
        const updateSLPFilters = (queryObject, replaceFilters = true) => {
            try {
                const context = searchContextRef.current;

                if (!queryObject || typeof queryObject !== 'object') {
                    console.error('SLP API: Invalid query object. Expected object format.');
                    return context?.query || {};
                }

                if (!context || !context.setQuery) {
                    console.error('SLP API: Search context not available.');
                    return {};
                }

                // Transform VWA format to SLP format
                const slpFormattedQuery = transformVWAtoSLPFormat(queryObject);

                let updatedFilters;

                if (replaceFilters) {
                    // Replace all current filters with new ones
                    updatedFilters = slpFormattedQuery;
                } else {
                    // Merge new filters with existing filters
                    const currentQuery = context.query || {};
                    updatedFilters = { ...currentQuery };

                    Object.keys(slpFormattedQuery).forEach(facetKey => {
                        const value = slpFormattedQuery[facetKey];

                        if (updatedFilters[facetKey]) {
                            // Merge with existing values for this facet
                            updatedFilters[facetKey] = [...new Set([...updatedFilters[facetKey], ...value])];
                        } else {
                            updatedFilters[facetKey] = value;
                        }
                    });
                }

                // Apply the updated filters
                context.setQuery(updatedFilters);

                return updatedFilters;
            } catch (error) {
                console.error('SLP API: Error updating filters', error);
                return searchContextRef.current?.query || {};
            }
        };

        // Expose API on window object
        window.hpSlpAPI = {
            updateFilters: updateSLPFilters,
            getCurrentFilters: () => {
                const context = searchContextRef.current;
                return context?.query ? { ...context.query } : {};
            },
            clearFilters: () => {
                const context = searchContextRef.current;
                if (context && context.setQuery) {
                    context.setQuery({});
                    return {};
                }
                return {};
            },
            getSearchKey: () => searchKey,
        };

        // Cleanup on unmount - only delete if it's still our API object
        return () => {
            if (window.hpSlpAPI && window.hpSlpAPI.updateFilters === updateSLPFilters) {
                delete window.hpSlpAPI;
            }
        };
    }, [searchKey]); // Only recreate if searchKey changes
};

export default useSLPExternalAPI;
